// timer.js
import { clamp, qs, qsa } from './utils.js';
import WakeLock from './wake-lock.js';

const TAKEOVER_CANCELLED = new Error('Timer takeover opening superseded');
const PAUSE_REASONS = new Set([
  'user-pause',
  'escape',
  'superseded',
  'confirmation',
  'visibility',
  'pagehide',
  'takeover-failure',
]);

export default class Timer {
  static _instances = new Set();
  static _activeTimer = null;
  static _takeoverTransition = null;
  static _visibilityHandler = null;
  static _pagehideHandler = null;
  static _reducedMotion = null;
  static _wakeLockSyncQueued = false;

  /** Creates a simple or interval timer from exercise element data attributes. @param {HTMLElement} el @param {{sound?: import('./sound-manager.js').default}} [options] */
  constructor(el, { sound } = {}) {
    this.el = el;
    this.sound = sound;
    this.total = Math.max(1, parseInt(el.getAttribute('data-duration-seconds') || '0', 10));
    this.prepTotal = Math.max(0, parseInt(el.getAttribute('data-start-delay-seconds') || '3', 10));
    this.work = parseInt(el.getAttribute('data-interval-work') || '0', 10);
    this.rest = parseInt(el.getAttribute('data-interval-rest') || '0', 10);
    this.rounds = parseInt(el.getAttribute('data-interval-rounds') || '0', 10);
    this.intervalMode = this.work > 0 && this.rounds > 0;
    this.display = qs('.ex-timer-display', el);
    this.status = qs('.ex-timer-status', el);
    // Pre-rendered by the template, one per round, and only up to a ceiling --
    // past about a dozen the row reads as specks and the status text carries
    // the position on its own. A simple timer has none.
    this.pips = qsa('.ex-timer-pip', el);
    this.announcement = qs('.ex-timer-announce', el);
    this.startBtn = qs('.timer-start', el);
    this.resetBtn = qs('.timer-reset', el);
    this.exerciseName = this._getExerciseName();
    this.running = false;
    this.phase = 'idle';
    this.remaining = this.total;
    this.prepRemaining = this.prepTotal;
    this.currentRound = 1;
    this.segment = 'work';
    this.segRemaining = this.work;
    this.h = null;
    this._destroyed = false;
    this._lifecycleVersion = 0;
    this._takeoverSnapshot = null;
    this._takeoverInvoker = null;

    this.update = this.update.bind(this);
    this.tick = this.tick.bind(this);
    this.start = this.start.bind(this);
    this.pause = this.pause.bind(this);
    this.reset = this.reset.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleTakeoverKeydown = this.handleTakeoverKeydown.bind(this);
    this.handleTakeoverFocusin = this.handleTakeoverFocusin.bind(this);
    this.handleConfirmationOpening = this.handleConfirmationOpening.bind(this);

    this.startBtn?.addEventListener('click', this.toggle);
    this.resetBtn?.addEventListener('click', this.reset);
    this.update();

    Timer._instances.add(this);
    el.__timer = this;
  }

  /** Defers a takeover request until the current opening or restoration transaction finishes. @private */
  static _deferTakeover(timer, action, invoker = timer.startBtn) {
    const request = { timer, action, invoker, result: false };
    const transition = this._takeoverTransition;
    if (transition) {
      transition.successor = request;
      if (transition.phase === 'opening') transition.cancelled = true;
      return request;
    }
    const active = this._activeTimer;
    if (!active || active === timer) return null;

    this._takeoverTransition = {
      timer: active,
      phase: 'restoring',
      restoring: false,
      cancelled: false,
      successor: request,
    };
    active.stop('superseded');
    return request;
  }

  /** Releases a transition before running its latest live successor request. @private */
  static _finishTakeoverTransition(transition) {
    if (this._takeoverTransition === transition) this._takeoverTransition = null;
    const request = transition.successor;
    transition.successor = null;
    if (!request || request.timer._destroyed) return;
    if (request.action === 'start') request.timer.start();
    else request.result = request.timer.openTakeover(request.invoker);
  }

  /** Renders the current phase and emits a timer:update event for UI consumers. @returns {void} */
  update() {
    let t;
    // How long the segment on screen runs in total, so the ring can show how
    // far through it we are rather than just how much is left.
    let span;
    if (this.phase === 'idle') {
      t = this.intervalMode ? this.work : this.total;
      span = t;
    } else if (this.phase === 'prepare') {
      t = this.prepRemaining;
      span = this.prepTotal;
    } else if (this.intervalMode) {
      t = this.segRemaining;
      span = this.segment === 'rest' ? this.rest : this.work;
    } else {
      t = this.remaining;
      span = this.total;
    }
    if (this.display) this._paintDisplay(t);
    this._paintRing(t, span);
    this._paintPips();
    // Mid-run but not ticking means the user paused (or the tab was hidden).
    const midRun = this.phase === 'prepare' || this.phase === 'active';
    const paused = midRun && !this.running;
    const { parts, spoken } = this._statusText({ paused });
    this._paintStatus(parts);
    if (this.announcement && this.announcement.textContent !== spoken) {
      this.announcement.textContent = spoken;
    }
    // `is-paused` hides the fullscreen overlay without changing the state machine.
    this.el.classList.remove('is-prep', 'is-work', 'is-rest');
    this.el.classList.toggle('is-paused', paused);
    if (this.phase === 'prepare') {
      this.el.classList.add('is-prep');
    } else if (this.intervalMode) {
      if (this.phase === 'active') {
        this.el.classList.add(this.segment === 'rest' ? 'is-rest' : 'is-work');
      }
    } else if (this.phase === 'active') {
      this.el.classList.add('is-work');
    }
    this.el.dispatchEvent(
      new CustomEvent('timer:update', {
        bubbles: true,
        detail: {
          phase: this.phase,
          segment: this.segment,
          round: this.currentRound,
          remaining: this.intervalMode ? this.segRemaining : this.remaining,
        },
      })
    );
  }

  /**
   * @private Writes the clock, with the minutes in their own span.
   *
   * On a countdown that never passes a minute, `00:` is four fifths of the
   * readout and carries none of it. Splitting it out lets the stylesheet set
   * it smaller so the seconds -- the thing actually being watched -- own the
   * dial, without the JS having to know anything about how big either is.
   *
   * Dirty-checked off a data attribute for the same reason `_paintStatus` is:
   * this runs once a second and the element is rebuilt rather than assigned.
   * @param {number} t seconds remaining
   */
  _paintDisplay(t) {
    const m = String(Math.floor(t / 60)).padStart(2, '0');
    const s = String(t % 60).padStart(2, '0');
    const key = `${m}:${s}`;
    if (this.display.dataset.time === key) return;
    this.display.dataset.time = key;
    this.display.textContent = '';
    const mins = document.createElement('span');
    mins.className = 'ex-timer-display-mins';
    mins.textContent = `${m}:`;
    this.display.append(mins, s);
  }

  /**
   * @private Publishes how far through the current segment we are as a
   * percentage, which the stylesheet spends on the ring around the dial.
   *
   * Elapsed rather than remaining: the ring fills as the segment runs, so it
   * reads the same way a progress bar does everywhere else in the app.
   *
   * The value is published one tick ahead of the clock. The stylesheet eases
   * it over exactly one second, so a ring given the true elapsed time would
   * spend that whole second catching up and sit up to a full second behind the
   * digits beside it. Aiming at where the next tick lands means the sweep
   * arrives exactly as the number changes.
   * @param {number} remaining @param {number} span
   */
  _paintRing(remaining, span) {
    if (!this.el) return;
    // A ticking ring is published one tick ahead so the sweep lands on the
    // value the digits already show instead of trailing it by a second. A
    // timer that is not ticking has nothing to lead and sits on the truth.
    const lead = this.running && Timer.ringInterpolates() ? 1 : 0;
    const settled = span > 0 ? clamp((span - remaining) / span, 0, 1) : 0;
    const elapsed = span > 0 ? clamp((span - remaining + lead) / span, 0, 1) : 0;
    const previous = this._ringPct;
    this._ringPct = elapsed;

    /* A new segment sends the ring from full back to empty, and a transition
       would unwind it anticlockwise over a second while the digits have
       already restarted. Committing the segment's real starting value with the
       transition off both skips that unwind and gives the lead something to
       sweep out of, so the round opens on an empty ring rather than a tick
       already filled. Reading `offsetWidth` is what forces that commit, once
       per segment rather than once per tick. */
    if (previous !== undefined && elapsed < previous) {
      this.el.style.transitionProperty = 'none';
      this.el.style.setProperty('--timer-pct', `${(settled * 100).toFixed(2)}%`);
      void this.el.offsetWidth;
      this.el.style.transitionProperty = '';
      if (settled === elapsed) return;
    }
    this.el.style.setProperty('--timer-pct', `${(elapsed * 100).toFixed(2)}%`);
  }

  /**
   * Whether the ring eases between ticks, which is what makes publishing it a
   * tick early correct. Under reduced motion it steps, and the honest value to
   * show is the one the clock is actually on.
   * @returns {boolean}
   */
  static ringInterpolates() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    this._reducedMotion ||= window.matchMedia('(prefers-reduced-motion: reduce)');
    return !this._reducedMotion.matches;
  }

  /**
   * @private Fills one pip per round reached, current round included.
   *
   * The pips answer "where am I in the block" at a glance, which the status
   * text was doing in words. `currentRound` does not advance until rest ends,
   * so during rest the round just worked stays lit -- which is the honest
   * reading: that round is behind you.
   */
  _paintPips() {
    if (!this.pips?.length) return;
    const counting = this.phase === 'active' || this.phase === 'done';
    const filled = counting ? clamp(this.currentRound, 0, this.pips.length) : 0;
    if (this._pipsFilled === filled) return;
    this._pipsFilled = filled;
    this.pips.forEach((pip, index) => pip.classList.toggle('is-on', index < filled));
  }

  /**
   * @private Writes the visible status, keeping its connective words in a
   * de-emphasised span so the digits carry the glance.
   *
   * A single-part status is written straight to `textContent`, the way this
   * always did it: it is one DOM write instead of a clear plus two appends, and
   * the status is repainted on every tick.
   * @param {Array<{text: string, quiet?: boolean}>} parts
   */
  _paintStatus(parts) {
    if (!this.status) return;
    // Dirty-check off an attribute rather than off `textContent`, which no
    // longer round-trips once part of the status is wrapped in a child.
    const key = parts.map((part) => (part.quiet ? `~${part.text}` : part.text)).join(' ');
    if (this.status.dataset.status === key) return;
    this.status.dataset.status = key;
    if (!parts.some((part) => part.quiet)) {
      this.status.textContent = parts.map((part) => part.text).join(' ');
      return;
    }
    this.status.textContent = '';
    parts.forEach((part, index) => {
      if (index > 0) this.status.append(' ');
      if (!part.quiet) {
        this.status.append(part.text);
        return;
      }
      const quiet = document.createElement('span');
      quiet.className = 'ex-timer-status-quiet';
      quiet.textContent = part.text;
      this.status.append(quiet);
    });
  }

  /**
   * @private Returns what the takeover shows and what it announces for the
   * current phase. The two differ on purpose: the panel is read at arm's length
   * mid-effort and wants the shortest thing that answers "where am I", while
   * the live region is heard rather than seen and wants a sentence.
   *
   * The visible half comes back as parts rather than a string because the words
   * between the digits are scaffolding -- "of", "to go" -- and are set smaller
   * so a glance lands on the numbers.
   * @param {{paused: boolean}} state
   * @returns {{parts: Array<{text: string, quiet?: boolean}>, spoken: string}}
   */
  _statusText({ paused }) {
    if (paused) return { parts: [{ text: 'Paused' }], spoken: 'Paused' };
    if (this.phase === 'prepare') return { parts: [{ text: 'Get ready' }], spoken: 'Get ready' };
    if (this.phase === 'done')
      return { parts: [{ text: 'Timer complete' }], spoken: 'Timer complete' };
    if (this.phase !== 'active') return { parts: [], spoken: '' };

    if (!this.intervalMode) {
      // A single-duration timer used to show a clock and nothing else, so the
      // screen gave no clue which exercise it belonged to once it covered the
      // card that launched it.
      return { parts: [{ text: this.exerciseName }], spoken: this.exerciseName };
    }

    if (this.segment === 'work') {
      // Only "of" shrinks here. Both numbers are the message -- which round,
      // out of how many -- and de-emphasising the total would answer half the
      // question.
      return {
        parts: [
          { text: `${this.currentRound}` },
          { text: 'of', quiet: true },
          { text: `${this.rounds}` },
        ],
        spoken: `Work, round ${this.currentRound} of ${this.rounds}`,
      };
    }

    // Rest counts down the work still owed rather than repeating the round
    // number, because that is the question actually being asked during it.
    // `currentRound` does not advance until rest ends, so the round just
    // finished is the one still numbered, and what remains is everything above
    // it. The final round has a rest segment too when one is configured, and
    // "0 to go" is a worse way of saying the work is over.
    const remaining = Math.max(0, this.rounds - this.currentRound);
    if (remaining === 0)
      return { parts: [{ text: 'Last round done' }], spoken: 'Rest, last round done' };
    return {
      parts: [{ text: `${remaining}` }, { text: 'to go', quiet: true }],
      spoken: `Rest, ${remaining} work ${remaining === 1 ? 'round' : 'rounds'} to go`,
    };
  }

  /** @private Announces that this timer's exercise work segment has completed. */
  _completeExercise() {
    this.el.dispatchEvent(
      new CustomEvent('timer:complete-exercise', {
        bubbles: true,
        detail: { source: 'timer' },
      })
    );
  }

  /** Returns the exercise name from the template-provided control label or labelled card heading. @returns {string} */
  _getExerciseName() {
    const label = this.startBtn?.getAttribute('aria-label');
    const labelMatch = label?.match(/^(?:Start|Pause) (.+) timer$/);
    if (labelMatch) return labelMatch[1];

    const card = this.el.closest('.exercise-card');
    const headingId = card?.getAttribute('aria-labelledby');
    const heading = headingId ? document.getElementById(headingId) : card?.querySelector('h3');
    return heading?.textContent.trim() || '';
  }

  /** Updates the primary timer control while preserving its exercise-specific accessible name. @param {'Start'|'Pause'} action @returns {void} */
  setPrimaryAction(action) {
    if (!this.startBtn) return;
    /* The label is a span so the button can also hold an icon. Writing
       `textContent` on the button itself -- which this did -- removes every
       child node, so the first press silently deleted the play mark and
       nothing brought it back. The fallback keeps timers built without the
       span working, which is how the unit test fixtures are shaped. */
    const label = this.startBtn.querySelector('.timer-start__label');
    if (label) label.textContent = action;
    else this.startBtn.textContent = action;
    if (this.exerciseName)
      this.startBtn.setAttribute('aria-label', `${action} ${this.exerciseName} timer`);
  }

  /** Returns each outside sibling branch between this timer and the document body. @returns {HTMLElement[]} */
  collectBackgroundBranches() {
    const branches = [];
    const seen = new Set();
    let current = this.el;

    while (current && current !== document.body) {
      const parent = current.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling !== current && !seen.has(sibling)) {
          seen.add(sibling);
          branches.push(sibling);
        }
      });
      current = parent;
    }

    return branches;
  }

  /** Returns enabled visible timer action controls in DOM order. @returns {HTMLButtonElement[]} */
  getTakeoverControls() {
    if (!this.el) return [];
    return qsa('.timer-start, .timer-reset', this.el).filter((control) => {
      if (control.disabled || control.hidden || control.getAttribute('aria-hidden') === 'true') {
        return false;
      }
      if (control.closest('[hidden]')) return false;
      const style = window.getComputedStyle(control);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  /** Focuses the primary takeover action, falling back to the dialog container. @returns {HTMLElement|null} */
  focusTakeover() {
    if (!this.el) return null;
    const target = this.getTakeoverControls()[0] || this.el;
    target.focus({ preventScroll: true });
    return target;
  }

  /** Contains keyboard navigation and handles Escape while this timer owns the takeover. @param {KeyboardEvent} event @returns {void} */
  handleTakeoverKeydown(event) {
    if (Timer._activeTimer !== this) return;
    if (event.key === 'Escape') {
      if (document.querySelector('#progress-dialog[open]')) return;
      event.preventDefault();
      this.stop('escape');
      return;
    }
    if (event.key !== 'Tab') return;

    const controls = this.getTakeoverControls();
    if (!controls.length) {
      event.preventDefault();
      this.focusTakeover();
      return;
    }

    const first = controls[0];
    const last = controls[controls.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.el.contains(active))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (active === last || !this.el.contains(active))) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  /** Releases modality so an opening confirmation is the only focus owner. @returns {void} */
  handleConfirmationOpening() {
    if (Timer._activeTimer === this) this.stop('confirmation');
  }

  /** Redirects programmatic focus that escapes the active takeover. @param {FocusEvent} event @returns {void} */
  handleTakeoverFocusin(event) {
    if (Timer._activeTimer === this && this.el && !this.el.contains(event.target)) {
      this.focusTakeover();
    }
  }

  /** Restores the exact DOM and listener state captured before takeover setup. @param {object|null} [snapshot=this._takeoverSnapshot] @returns {void} */
  restoreTakeoverSnapshot(snapshot = this._takeoverSnapshot) {
    if (!snapshot) return;
    let restorationError = null;
    const attempt = (restore) => {
      try {
        restore();
      } catch (error) {
        restorationError ||= error;
      }
    };

    snapshot.listeners.forEach(({ type, listener, options }) => {
      attempt(() => document.removeEventListener(type, listener, options));
    });
    snapshot.branches.forEach(({ el, inert, inertAttribute, ariaHidden }) => {
      if (snapshot.supportsInert) attempt(() => (el.inert = inert.value));
      attempt(() => {
        if (inertAttribute.present) el.setAttribute('inert', inertAttribute.value);
        else el.removeAttribute('inert');
      });
      attempt(() => {
        if (ariaHidden.present) el.setAttribute('aria-hidden', ariaHidden.value);
        else el.removeAttribute('aria-hidden');
      });
    });
    Object.entries(snapshot.timerAttributes).forEach(([name, state]) => {
      attempt(() => {
        if (state.present) snapshot.timerElement.setAttribute(name, state.value);
        else snapshot.timerElement.removeAttribute(name);
      });
    });

    if (this._takeoverSnapshot === snapshot) this._takeoverSnapshot = null;
    if (restorationError) console.warn('[timer] Takeover restoration failed', restorationError);
  }

  /** Opens the fullscreen timer as a reversible modal takeover. @param {HTMLElement|null} [invoker=this.startBtn] @returns {boolean} */
  openTakeover(invoker = this.startBtn) {
    if (this._destroyed) return false;
    const currentTransition = Timer._takeoverTransition;
    if (currentTransition?.timer === this && currentTransition.phase === 'opening') return true;
    const deferred = Timer._deferTakeover(this, 'open', invoker);
    if (deferred) return deferred.result;
    if (this._takeoverSnapshot) return true;
    if (!this.el) return false;

    const transaction = {
      timer: this,
      phase: 'opening',
      restoring: false,
      cancelled: false,
      successor: null,
    };
    Timer._takeoverTransition = transaction;
    const ensureReserved = () => {
      if (transaction.cancelled || Timer._takeoverTransition !== transaction) {
        throw TAKEOVER_CANCELLED;
      }
    };
    const readAttribute = (element, name) => {
      const present = element.hasAttribute(name);
      ensureReserved();
      const value = element.getAttribute(name);
      ensureReserved();
      return { present, value };
    };
    let transactionSnapshot = null;

    try {
      const attributes = ['role', 'aria-modal', 'aria-labelledby', 'tabindex'];
      const supportsInert = 'inert' in HTMLElement.prototype;
      const timerAttributes = {};
      attributes.forEach((name) => {
        timerAttributes[name] = readAttribute(this.el, name);
      });
      const branches = this.collectBackgroundBranches();
      ensureReserved();
      const branchSnapshots = [];
      branches.forEach((branch) => {
        const inert = supportsInert ? branch.inert : undefined;
        ensureReserved();
        branchSnapshots.push({
          el: branch,
          inert: { value: inert },
          inertAttribute: readAttribute(branch, 'inert'),
          ariaHidden: readAttribute(branch, 'aria-hidden'),
        });
      });
      const snapshot = {
        supportsInert,
        timerElement: this.el,
        timerAttributes,
        branches: branchSnapshots,
        listeners: [],
      };
      transactionSnapshot = snapshot;
      ensureReserved();
      this._takeoverSnapshot = snapshot;
      this._takeoverInvoker = invoker;

      snapshot.branches.forEach(({ el }) => {
        if (supportsInert) {
          el.inert = true;
          ensureReserved();
          el.setAttribute('inert', '');
          ensureReserved();
        } else {
          el.setAttribute('aria-hidden', 'true');
          ensureReserved();
        }
      });

      const listeners = [
        { type: 'keydown', listener: this.handleTakeoverKeydown, options: true },
        { type: 'focusin', listener: this.handleTakeoverFocusin, options: true },
        {
          type: 'confirmation:opening',
          listener: this.handleConfirmationOpening,
          options: false,
        },
      ];
      listeners.forEach(({ type, listener, options }) => {
        snapshot.listeners.push({ type, listener, options });
        document.addEventListener(type, listener, options);
        ensureReserved();
      });

      const card = this.el.closest('.exercise-card');
      ensureReserved();
      const headingId = card?.getAttribute('aria-labelledby');
      ensureReserved();
      this.el.setAttribute('role', 'dialog');
      ensureReserved();
      this.el.setAttribute('aria-modal', 'true');
      ensureReserved();
      if (headingId) {
        this.el.setAttribute('aria-labelledby', headingId);
      } else {
        this.el.removeAttribute('aria-labelledby');
      }
      ensureReserved();
      this.el.setAttribute('tabindex', '-1');
      ensureReserved();
      this.focusTakeover();
      ensureReserved();
      Timer._activeTimer = this;
      Timer._finishTakeoverTransition(transaction);
      return true;
    } catch (error) {
      transaction.phase = 'restoring';
      transaction.restoring = true;
      this.restoreTakeoverSnapshot(transactionSnapshot);
      if (Timer._activeTimer === this) Timer._activeTimer = null;
      this._takeoverInvoker = null;
      this.stop('takeover-failure');
      if (error !== TAKEOVER_CANCELLED) console.warn('[timer] Takeover setup failed', error);
      Timer._finishTakeoverTransition(transaction);
      return false;
    }
  }

  /** Closes the active takeover and optionally restores its connected invoker. @param {{restoreFocus?: boolean}} [options] @returns {void} */
  closeTakeover({ restoreFocus = true } = {}) {
    let transition = Timer._takeoverTransition;
    const ownsTakeover =
      Boolean(this._takeoverSnapshot) || Timer._activeTimer === this || transition?.timer === this;
    if (!ownsTakeover) {
      if (Timer._activeTimer === this) Timer._activeTimer = null;
      return;
    }
    if (transition?.timer === this && transition.phase === 'opening') {
      transition.cancelled = true;
      return;
    }
    if (transition?.timer === this && transition.restoring) return;
    if (!transition) {
      transition = {
        timer: this,
        phase: 'restoring',
        restoring: false,
        cancelled: false,
        successor: null,
      };
      Timer._takeoverTransition = transition;
    } else if (transition.timer === this) {
      transition.cancelled = true;
      transition.phase = 'restoring';
    } else {
      return;
    }
    transition.restoring = true;
    if (!this._takeoverSnapshot) {
      if (Timer._activeTimer === this) Timer._activeTimer = null;
      Timer._finishTakeoverTransition(transition);
      return;
    }
    const invoker = this._takeoverInvoker;
    this.restoreTakeoverSnapshot();
    const shouldRestoreFocus =
      restoreFocus &&
      !transition.successor &&
      !this._destroyed &&
      Timer._takeoverTransition === transition &&
      transition.timer === this &&
      Timer._activeTimer === this &&
      invoker?.isConnected &&
      !document.hidden &&
      !document.querySelector('#progress-dialog[open], [aria-modal="true"]');
    if (Timer._activeTimer === this) Timer._activeTimer = null;
    this._takeoverInvoker = null;
    if (shouldRestoreFocus) {
      try {
        invoker.focus({ preventScroll: true });
      } catch (error) {
        console.warn('[timer] Takeover focus restoration failed', error);
      }
    }
    Timer._finishTakeoverTransition(transition);
  }

  /** Advances the idle/prepare/active/done state machine by one second. @returns {void} */
  tick() {
    if (!this.running) return;
    if (this.phase === 'prepare') {
      this.prepRemaining = clamp(this.prepRemaining - 1, 0, this.prepTotal);
      if (this.prepRemaining > 0) this.sound?.prepTick(this.prepRemaining);
      this.update();
      if (this.prepRemaining <= 0) {
        this.phase = 'active';
        if (this.intervalMode) {
          this.segment = 'work';
          this.segRemaining = this.work;
          this.currentRound = 1;
        } else {
          this.remaining = this.total;
        }
        this.sound?.startWork();
        this.el.dispatchEvent(
          new CustomEvent('timer:workstart', { detail: { round: this.currentRound } })
        );
        this.update();
      }
      return;
    }
    if (this.phase !== 'active') return;
    if (this.intervalMode) {
      this.segRemaining = clamp(this.segRemaining - 1, 0, 60 * 360);
      this.update();
      if (this.segRemaining <= 0) {
        if (this.segment === 'work') {
          if (this.rest <= 0 && this.currentRound >= this.rounds) return this.stop('complete');
          // Completion handlers run synchronously and may reset this timer while advancing
          // its block. Recheck running before entering rest or scheduling more sound.
          this._completeExercise();
          if (!this.running) return;
          if (this.rest > 0) {
            this.segment = 'rest';
            this.segRemaining = this.rest;
            this.sound?.restTransition();
            this.el.dispatchEvent(
              new CustomEvent('timer:reststart', { detail: { round: this.currentRound } })
            );
            this.update();
            return;
          } else {
            this.currentRound++;
            if (this.currentRound > this.rounds) return this.finishAll();
            this.segment = 'work';
            this.segRemaining = this.work;
            this.sound?.startWork();
            this.el.dispatchEvent(
              new CustomEvent('timer:workstart', { detail: { round: this.currentRound } })
            );
            this.update();
            return;
          }
        } else {
          this.currentRound++;
          if (this.currentRound > this.rounds) return this.finishAll();
          this.segment = 'work';
          this.segRemaining = this.work;
          this.sound?.startWork();
          this.el.dispatchEvent(
            new CustomEvent('timer:workstart', { detail: { round: this.currentRound } })
          );
          this.update();
          return;
        }
      }
      return;
    }
    this.remaining = clamp(this.remaining - 1, 0, this.total);
    this.update();
    if (this.remaining <= 0) this.stop('complete');
  }

  /** Starts a fresh run or resumes a paused one; only fresh runs emit timer:restart. @returns {void} */
  start() {
    if (this._destroyed || this.running) return;
    if (Timer._deferTakeover(this, 'start')) return;
    this._lifecycleVersion++;
    // Fresh starts alone revoke exercise completion; resumes retain earned progress.
    const fresh = this.phase === 'idle' || this.phase === 'done';
    if (fresh) {
      this.phase = this.prepTotal > 0 ? 'prepare' : 'active';
      this.prepRemaining = this.prepTotal;
      if (this.phase === 'active') {
        if (this.intervalMode) {
          this.segment = 'work';
          this.segRemaining = this.work;
          this.currentRound = 1;
        } else {
          this.remaining = this.total;
        }
      }
    }
    this.running = true;
    this.setPrimaryAction('Pause');
    if (!this.openTakeover(this.startBtn)) return;
    if (fresh && this.phase === 'active') this.sound?.startWork();
    this.h = window.setInterval(() => this.tick(), 1000);
    Timer.syncWakeLock();
    if (fresh) this.el.dispatchEvent(new CustomEvent('timer:restart'));
    this.update();
  }

  /**
   * Holds the screen awake for exactly as long as some timer is still ticking.
   *
   * Driven off the whole instance set rather than off the timer that happened
   * to change, because one timer starting supersedes another: pairing an
   * acquire with a release per instance would drop the lock in the handover.
   * @returns {void}
   */
  static syncWakeLock() {
    // Deferred to the end of the turn because a handover is a stop immediately
    // followed by a start: reading the set on the stop alone would see nothing
    // running and give the screen back a microtask before asking for it again.
    if (this._wakeLockSyncQueued) return;
    this._wakeLockSyncQueued = true;
    Promise.resolve().then(() => {
      this._wakeLockSyncQueued = false;
      const anyRunning = [...this._instances].some((timer) => timer.running);
      if (anyRunning) WakeLock.acquire();
      else WakeLock.release();
    });
  }

  /** Pauses ticking without discarding the current phase or remaining time. @returns {void} */
  pause({ restoreFocus = true } = {}) {
    this.stop(restoreFocus ? 'user-pause' : 'superseded');
  }

  /**
   * Stops active lifecycle work without routing terminal states through a paused render.
   * @param {'user-pause'|'escape'|'complete'|'reset'|'superseded'|'confirmation'|'visibility'|'pagehide'|'destroy'|'takeover-failure'} reason
   * @param {{completeExercise?: boolean}} [options]
   * @returns {void}
   */
  stop(reason = 'user-pause', { completeExercise = true } = {}) {
    if (this._destroyed && reason !== 'destroy') return;
    const transition = Timer._takeoverTransition;
    const ownsTakeover =
      Boolean(this._takeoverSnapshot) || Timer._activeTimer === this || transition?.timer === this;
    if (!this.running && this.h === null && !ownsTakeover) {
      if (PAUSE_REASONS.has(reason)) return;
      if (reason === 'complete' && this.phase === 'done') return;
      if (reason === 'reset' && this.phase === 'idle') return;
    }

    const lifecycleVersion = ++this._lifecycleVersion;
    if (ownsTakeover) {
      if (!transition) {
        Timer._takeoverTransition = {
          timer: this,
          phase: 'restoring',
          restoring: false,
          cancelled: false,
          successor: null,
        };
      } else if (transition.timer === this) {
        if (transition.phase === 'opening') transition.cancelled = true;
        else transition.phase = 'restoring';
      }
    }

    if (this.h !== null) {
      clearInterval(this.h);
      this.h = null;
    }
    this.running = false;
    Timer.syncWakeLock();

    if (reason === 'destroy') {
      this.sound?.stopAll();
      this.closeTakeover({ restoreFocus: false });
      return;
    }

    this.setPrimaryAction('Start');
    if (reason === 'reset') {
      this.sound?.stopAll();
      this.phase = 'idle';
      this.remaining = this.total;
      this.prepRemaining = this.prepTotal;
      if (this.intervalMode) {
        this.currentRound = 1;
        this.segment = 'work';
        this.segRemaining = this.work;
      }
      this.el?.classList.remove('finished');
    } else if (reason === 'complete') {
      this.phase = 'done';
      this.el?.classList.add('finished');
      if (completeExercise) this._completeExercise();
      if (this._destroyed || this._lifecycleVersion !== lifecycleVersion) return;
    }

    if (this.el) this.update();
    if (this._destroyed || this._lifecycleVersion !== lifecycleVersion) return;

    if (reason === 'complete') {
      this.sound?.timerDone();
      this.el?.dispatchEvent(new CustomEvent('timer:done'));
      if (this._destroyed || this._lifecycleVersion !== lifecycleVersion) return;
    }

    const restoreFocus =
      reason === 'user-pause' || reason === 'escape' || reason === 'complete' || reason === 'reset';
    this.closeTakeover({ restoreFocus });
  }

  /** Returns this timer to its initial idle phase and stops active sound nodes. @returns {void} */
  reset() {
    this.stop('reset');
  }

  /** Forces an interval timer to done after its final work-segment completion. @returns {void} */
  finishAll() {
    this.stop('complete', { completeExercise: false });
  }

  /** Starts or pauses according to the current running state. @returns {void} */
  toggle() {
    this.running ? this.stop('user-pause') : this.start();
  }

  /** Detaches controls and releases this timer instance. @returns {void} */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    const transition = Timer._takeoverTransition;
    if (transition?.successor?.timer === this) transition.successor = null;
    if (transition?.timer === this) {
      transition.cancelled = true;
      transition.successor = null;
    }
    this.stop('destroy');
    this.startBtn?.removeEventListener('click', this.toggle);
    this.resetBtn?.removeEventListener('click', this.reset);
    Timer._instances.delete(this);
    if (this.el) {
      delete this.el.__timer;
    }
    this.el = null;
    this.display = null;
    this.status = null;
    this.pips = null;
    this.announcement = null;
    this.startBtn = null;
    this.resetBtn = null;
    this.sound = null;
    if (Timer._activeTimer === this) Timer._activeTimer = null;
    if (Timer._instances.size === 0) Timer.teardownLifecycleListeners();
  }

  /** Instantiates timers for compatible exercise elements below a root. @param {Document|Element} [root=document] @param {import('./sound-manager.js').default} [sound] @returns {void} */
  static initAll(root = document, sound) {
    qsa('.ex-timer', root).forEach((el) => {
      const isSimple = el.hasAttribute('data-duration-seconds');
      const isInterval =
        el.hasAttribute('data-interval-work') && el.hasAttribute('data-interval-rounds');
      if (!el.__timer && (isSimple || isInterval)) new Timer(el, { sound });
    });
  }

  /** Destroys every registered timer. @returns {void} */
  static destroyAll() {
    Timer._instances.forEach((t) => t.destroy());
    Timer._instances.clear();
    Timer._activeTimer = null;
    Timer._takeoverTransition = null;
    Timer.teardownLifecycleListeners();
  }

  /** Wires one visibility handler that pauses timers in hidden tabs. @returns {void} */
  static initVisibilityPauseOnce() {
    if (this._visibilityHandler) return;
    this._visibilityHandler = () => {
      if (document.hidden) Timer._instances.forEach((t) => t.stop('visibility'));
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  /** Wires one pagehide handler to pause timers before navigation. @returns {void} */
  static initPageUnloadCleanup() {
    if (this._pagehideHandler) return;
    this._pagehideHandler = () => {
      Timer._instances.forEach((t) => t.stop('pagehide'));
    };
    window.addEventListener('pagehide', this._pagehideHandler);
  }

  /** Detaches global lifecycle listeners so a later timer initialization can wire fresh ones. */
  static teardownLifecycleListeners() {
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (this._pagehideHandler) {
      window.removeEventListener('pagehide', this._pagehideHandler);
      this._pagehideHandler = null;
    }
  }
}
